import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { MessageSquare, Send, Trash2, Loader2, Check, Users, X, Pin } from 'lucide-react';

/**
 * Panneau enseignant : publier des posts « Actu de la vie & du fun »
 * visibles par ses élèves rattachés (globalement ou épinglés à certains).
 */
export default function TeacherActuPanel() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [accent, setAccent] = useState('emerald');
  const [posts, setPosts] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [pinningId, setPinningId] = useState(null);
  const [pinnedIds, setPinnedIds] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.teacherActuList(), api.teacherStudentsLite()]);
      setPosts(p.items || []);
      setStudents(Array.isArray(s) ? s : []);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) { setErr('Titre et message obligatoires'); return; }
    setSaving(true); setErr(''); setMsg('');
    try {
      await api.teacherActuCreate({ type: 'post_enseignant', title: title.trim(), body: body.trim(), accent });
      setTitle(''); setBody(''); setAccent('emerald');
      setMsg('Publié ! Tes élèves vont le voir sur leur tableau de bord.');
      await refresh();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erreur lors de la publication');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce post ?')) return;
    try { await api.teacherActuDelete(id); await refresh(); } catch (_) { /* silent */ }
  };

  const openPin = (post) => {
    setPinningId(post.id);
    setPinnedIds([]); // À terme : récupérer depuis le post, ici on repart d'une base globale
  };

  const savePin = async () => {
    try {
      await api.teacherActuPin(pinningId, pinnedIds);
      setPinningId(null);
      setMsg(pinnedIds.length === 0 ? 'Post visible par TOUS tes élèves.' : `Post ciblé sur ${pinnedIds.length} élève(s).`);
      setTimeout(() => setMsg(''), 3000);
      await refresh();
    } catch (_) { /* silent */ }
  };

  const toggleStudent = (id) => {
    setPinnedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const accents = [
    { key: 'emerald', label: 'Positif', clr: 'bg-emerald-500' },
    { key: 'amber',   label: 'Attention', clr: 'bg-amber-500' },
    { key: 'red',     label: 'Important', clr: 'bg-red-500' },
    { key: 'sky',     label: 'Info',    clr: 'bg-sky-500' },
    { key: 'violet',  label: 'Motivation', clr: 'bg-violet-500' },
  ];

  return (
    <div className="rounded-2xl border border-blue-900/30 bg-gradient-to-br from-blue-950/30 to-[#0d0d0e] p-5 sm:p-6" data-testid="teacher-actu-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-blue-400 font-bold mb-1 inline-flex items-center gap-1.5">
            <MessageSquare size={12} /> Message du jour
          </div>
          <h3 className="font-serif-editorial text-white text-[22px]">Publie un mot pour tes élèves</h3>
          <p className="text-white/50 text-sm mt-1">Encouragement, info du jour, défi personnalisé — visible directement sur leur tableau de bord.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titre court (ex : Bravo à tous cette semaine !)"
          maxLength={80}
          data-testid="teacher-actu-title-input"
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-blue-500/50"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Ton message (max 500 caractères)..."
          maxLength={500}
          rows={4}
          data-testid="teacher-actu-body-input"
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-blue-500/50 resize-none"
        />
        <div className="flex items-center gap-2 flex-wrap">
          {accents.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => setAccent(a.key)}
              data-testid={`teacher-actu-accent-${a.key}`}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 transition ${accent === a.key ? 'bg-white/15 border border-white/30 text-white' : 'bg-white/5 border border-white/10 text-white/60 hover:text-white'}`}
            >
              <span className={`w-2 h-2 rounded-full ${a.clr}`} />
              {a.label}
            </button>
          ))}
          <div className="ml-auto text-[11px] text-white/40">{body.length}/500</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="teacher-actu-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            Publier
          </button>
          {msg && <div className="text-[13px] text-emerald-400 inline-flex items-center gap-1"><Check size={14} /> {msg}</div>}
          {err && <div className="text-[13px] text-red-400">{err}</div>}
        </div>
      </form>

      {/* Liste posts */}
      {loading ? (
        <div className="mt-6 text-white/40 text-sm">Chargement…</div>
      ) : posts.length > 0 && (
        <div className="mt-6">
          <h4 className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-3">Tes posts récents ({posts.length})</h4>
          <div className="space-y-2">
            {posts.map((p) => (
              <div key={p.id} className="rounded-xl bg-white/[0.03] border border-white/10 p-3 flex items-start gap-3" data-testid={`teacher-actu-post-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-semibold truncate">{p.title}</div>
                  <div className="text-white/60 text-[13px] mt-1 line-clamp-2">{p.body}</div>
                  <div className="text-[10px] text-white/40 mt-1">
                    {new Date(p.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                  </div>
                </div>
                <button
                  onClick={() => openPin(p)}
                  data-testid={`teacher-actu-pin-${p.id}`}
                  className="p-2 rounded-lg text-white/50 hover:text-blue-400 hover:bg-white/5 transition"
                  title="Cibler des élèves"
                >
                  <Pin size={14} />
                </button>
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`teacher-actu-delete-${p.id}`}
                  className="p-2 rounded-lg text-white/50 hover:text-red-400 hover:bg-white/5 transition"
                  title="Supprimer"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal ciblage */}
      {pinningId && (
        <div className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPinningId(null)}>
          <div className="w-full max-w-md rounded-2xl bg-[#111] border border-white/15 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-serif-editorial text-[20px]">Cibler des élèves</h4>
              <button onClick={() => setPinningId(null)} className="text-white/50 hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-white/60 text-sm mb-4">Coche des élèves pour envoyer <strong>uniquement à eux</strong>. Laisse vide pour envoyer à <strong>tous</strong>.</p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {students.length === 0 && <div className="text-white/40 text-sm text-center py-6">Aucun élève rattaché.</div>}
              {students.map((s) => (
                <label key={s.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pinnedIds.includes(s.id)}
                    onChange={() => toggleStudent(s.id)}
                    className="w-4 h-4 accent-blue-500"
                  />
                  <div className="flex-1">
                    <div className="text-white text-sm">{s.name}</div>
                    <div className="text-white/40 text-[11px]">{s.email}</div>
                  </div>
                </label>
              ))}
            </div>
            <button
              onClick={savePin}
              data-testid="teacher-actu-pin-save"
              className="mt-5 w-full py-2.5 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm inline-flex items-center justify-center gap-2"
            >
              <Users size={14} />
              {pinnedIds.length === 0 ? 'Envoyer à TOUS mes élèves' : `Envoyer à ${pinnedIds.length} élève(s)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
